const getHostnameFromUrl = (url: string): string => new URL(url).hostname;

/**
 * Returns the hostname of the top-most browsing context (the URL shown in the
 * browser address bar), regardless of how deeply nested in iframes we are.
 *
 * Resolution order:
 * 1. Not framed -> current hostname.
 * 2. Same-origin ancestors -> read window.top directly.
 * 3. Cross-origin -> last entry of ancestorOrigins (top-most ancestor, WebKit/Blink).
 * 4. Fallbacks -> immediate parent via referrer, then current hostname.
 */
export const getTopLevelHostname = (): string => {
  const isFramed = window.self !== window.top;

  if (!isFramed) {
    return document.location.hostname;
  }

  try {
    if (window.top?.location.hostname) {
      return window.top.location.hostname;
    }
  } catch {
    // Cross-origin access to window.top is blocked; fall through.
  }

  const ancestorOrigins = window.location.ancestorOrigins;
  // TODO - add to shared: `ancestorOrigins?.length` (ancestorOrigins is undefined in Firefox)
  if (ancestorOrigins.length) {
    const topMostOrigin = ancestorOrigins[ancestorOrigins.length - 1];
    if (topMostOrigin) {
      return getHostnameFromUrl(topMostOrigin);
    }
  }

  if (document.referrer) {
    return getHostnameFromUrl(document.referrer);
  }

  return document.location.hostname;
};
